/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { resourceUpdated, selectResourceById } from "../../slices/resourceCollection/resourceCollectionSlice";
import updateElement, { updateNarrativeElement } from "../../functions/updateElement";
import generateNarrativeForEvidenceVariableResource from "../generateNarrativeFunctions/generateNarrativeForEvidenceVariableResource";
import MarkdownEntry from "../../datatypeComponents/MarkdownEntry";
import StringEntry from "../../datatypeComponents/StringEntry";
import ArrayEntry from "../../datatypeComponents/ArrayEntry";
import CodeableReferenceEntry from "../../datatypeComponents/CodeableReferenceEntry";
import NarrativeEntry from "../../datatypeComponents/NarrativeEntry";
import CodeableConceptEntry from "../../datatypeComponents/CodeableConceptEntry";
import { evidenceVariableDefinitionConceptSystemChoices, evidenceVariableDotHandlingValueSet } from "../EvidenceVariableResourceDisplay";
import convertEvidenceVariableHandlingToCodeableConcept from "../resourceDisplayCommonFunctions/convertEvidenceVariableHandlingToCodeableConcept";

const EvidenceVariableResourceEdit = ({ foi }) => {

    const dispatch = useDispatch();
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceJson = parentResourceDetail?.revisedResourceJson;

    const [editResourceState, setEditResourceState] = useState({
        text: resourceJson.text,
        title: resourceJson.title,
        shortTitle: resourceJson.shortTitle,
        name: resourceJson.name,
        description: resourceJson.description,
        definition: resourceJson.definition,
        definitionModifier: resourceJson.definitionModifier,
        handling: convertEvidenceVariableHandlingToCodeableConcept(resourceJson.handling),
        category: resourceJson.category,
        relatesTo: resourceJson.relatesTo
    });
    const [containedResourceDictionary, setContainedResourceDictionary] = useState({});

    useEffect(() => {
        if (resourceJson) {
            if (Array.isArray(resourceJson.contained) && resourceJson.contained.length > 0) {
                let containedResourceDictionary = {};
                for (const item of resourceJson.contained) {
                    const key = "#" + item.id;
                    containedResourceDictionary[key] = item;
                }
                setContainedResourceDictionary(containedResourceDictionary);
            }
        }
    }, [resourceJson]);

    useEffect(() => {
        if (resourceJson) {
            updateNarrativeElement(editResourceState, "text", parentResourceDetail,
                dispatch);
        }
    }, [editResourceState.text]);

    useEffect(() => {
        if (editResourceState?.title !== undefined &&
            parentResourceDetail.revisedResourceJson.title !== editResourceState.title) {
            console.log("resourceUpdate for EvidenceVariable.title");
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            newResourceDetail.revisedResourceJson.title = editResourceState.title;
            newResourceDetail.displayTitle = editResourceState.title || editResourceState.shortTitle || editResourceState.name;
            dispatch(resourceUpdated(newResourceDetail));
        }
    }, [editResourceState.title]);

    useEffect(() => {
        if (editResourceState?.shortTitle !== undefined
            && !parentResourceDetail.revisedResourceJson.shortTitle !== editResourceState.shortTitle) {
            console.log("resourceUpdate for EvidenceVariable.shortTitle");
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            newResourceDetail.revisedResourceJson.shortTitle = editResourceState.shortTitle;
            newResourceDetail.displayTitle = editResourceState.title || editResourceState.shortTitle || editResourceState.name;
            dispatch(resourceUpdated(newResourceDetail));
        }
    }, [editResourceState.shortTitle]);

    useEffect(() => {
        if (editResourceState?.name !== undefined
            && parentResourceDetail.revisedResourceJson.name !== editResourceState.name) {
            console.log("resourceUpdate for EvidenceVariable.name");
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            newResourceDetail.revisedResourceJson.name = editResourceState.name;
            newResourceDetail.displayTitle = editResourceState.title || editResourceState.shortTitle || editResourceState.name;
            dispatch(resourceUpdated(newResourceDetail));
        }
    }, [editResourceState.name]);

    useEffect(() => {
        if (editResourceState) {
            updateElement(editResourceState, "description", "markdown", parentResourceDetail,
                dispatch);
        }
    }, [editResourceState.description]);

    useEffect(() => {
        if (editResourceState) {
            updateElement(editResourceState, "definition", "CodeableReference", parentResourceDetail,
                dispatch);
        }
    }, [editResourceState.definition]);

    useEffect(() => {
        if (editResourceState) {
            updateElement(editResourceState, "definitionModifier", "ArrayOfEvidenceVariableDefinitionModifier", parentResourceDetail,
                dispatch);
        }
    }, [editResourceState.definitionModifier]);

    useEffect(() => {
        if (editResourceState) {
            updateElement(editResourceState, "handling", "CodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [editResourceState.handling]);

    useEffect(() => {
        if (editResourceState) {
            updateElement(editResourceState, "category", "ArrayOfEvidenceVariableCategory", parentResourceDetail,
                dispatch);
        }
    }, [editResourceState.category]);

    useEffect(() => {
        if (editResourceState) {
            updateElement(editResourceState, "relatesTo", "ArrayOfRelatesTo", parentResourceDetail,
                dispatch);
        }
    }, [editResourceState.relatesTo]);

    return <div style={{ marginLeft: "24px" }}>
        {editResourceState && <>
            <StringEntry elementName='title'
                fieldLabel={'Title (Human-friendly Label for Variable Name)'}
                startingValue={editResourceState.title || ""}
                required={(resourceJson?.description || resourceJson?.name || resourceJson?.definition || resourceJson?.handling || resourceJson?.category)}
                setResourceState={setEditResourceState} />
            <StringEntry elementName='shortTitle'
                fieldLabel={'Short Title (for Variable Name)'}
                startingValue={editResourceState.shortTitle || ""}
                setResourceState={setEditResourceState} />
            <StringEntry elementName='name'
                fieldLabel={'Name (Machine Label for Variable Name)'}
                startingValue={editResourceState.name || ""}
                setResourceState={setEditResourceState} />
            <MarkdownEntry elementName='description' fieldLabel={'Description'}
                startingValue={editResourceState.description || ""}
                setResourceState={setEditResourceState} />
            <CodeableReferenceEntry elementName='definition'
                containedResourceDictionary={containedResourceDictionary}
                fieldLabel='Variable Definition'
                referencedResourceTypes={['Group']}
                enableCreation={true} systemChoices={evidenceVariableDefinitionConceptSystemChoices}
                systemChoicesOpen={true}
                startingValue={editResourceState.definition} setResourceState={setEditResourceState} />
            <ArrayEntry datatype='EvidenceVariableDefinitionModifier' elementName='definitionModifier'
                fieldLabel='Definition Modifier' startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed={true}
                startingValue={editResourceState.definitionModifier} setResourceState={setEditResourceState} />
            <CodeableConceptEntry elementName='handling' fieldLabel='Handling'
                startingValue={editResourceState.handling} setResourceState={setEditResourceState}
                codeableConceptLevelValueSet={evidenceVariableDotHandlingValueSet} />
            <ArrayEntry datatype='EvidenceVariableCategory' elementName='category'
                fieldLabel='Category' startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed={true}
                startingValue={editResourceState.category}
                setResourceState={setEditResourceState} />
            <p>Narrative Summary</p>
            <div style={{ marginLeft: "24px" }}>
                <NarrativeEntry elementName='text' fieldLabel='Narrative Summary'
                    generatedNarrative={generateNarrativeForEvidenceVariableResource(resourceJson)} startCollapsed
                    startingValue={editResourceState.text} setResourceState={setEditResourceState} />
            </div>
            <ArrayEntry datatype='RelatesTo' elementName='relatesTo'
                containedResourceDictionary={containedResourceDictionary}
                fieldLabel='Related Item' startEmptyArrayClosed={true} deletable={true}
                deletableArray={true} startCollapsed={true}
                startingValue={editResourceState.relatesTo}
                setResourceState={setEditResourceState} />
        </>}
    </div>
}

export default EvidenceVariableResourceEdit;